import clsx from "clsx";
import React, { isValidElement, type FunctionComponent } from "react";

import Ad from "@ui/components/ad/Ad";
import DangerouslySetCMSMarkup from "@ui/components/article/DangerouslySetCMSMarkup";
import LinkButton from "@ui/components/cta/LinkButton";
import { SELECTORS } from "@ui/config";
import { useComponentConfig } from "@ui/context/component-config-context";
import { useNavigationContext } from "@ui/context/navigation-context";
import useDefaults from "@ui/hooks/use-defaults";
import { sharedStyles } from "@ui/shared-styles";
import { useAnalytics } from "@voxmedia/duet-context/context/analytics-context";
import { useCommunityContext } from "@voxmedia/duet-context/context/community-context";
import { getResolvedHref } from "@voxmedia/duet-utils/url";

import * as styles from "./PageHeader.css";

type Link = {
  text: string;
  url: string;
  highlight?: boolean;
  event?: Record<any, any>;
};

export interface PageHeaderBaseConfig {
  borderBottom?: boolean | "standard" | "fancy";
  borderTop?: boolean | "standard" | "fancy";
  center?: boolean;
  className?: string;
  classNameDescription?: string;
  classNameLinks?: string;
  classNameSubhead?: string;
  classNameTitle?: string;
  classNameMoreLink?: string;
  classNameMoreLinkText?: string;
  classNameContainer?: string;
  headingLevel?: "h1" | "h2" | "h3" | "h4" | "h5";
  isBranded?: boolean;
  moreLinkIcon?: React.ReactNode;
  paddingBottom?: boolean;
  size?: "xsmall" | "small" | "medium" | "large";
  sponsorshipAdType?: "hubSponsorship";
  title: string | React.ReactNode | null;
  slug?: string;
  id?: string;
}

export interface Props extends Partial<PageHeaderBaseConfig> {
  subhead?: string | React.ReactNode | null;
  customSubhead?: React.ReactNode;
  description?: string | string[] | null;
  links?: Link[];
  moreLink?: Link;
  componentContext?: string;
  titlePrefix?: React.ReactNode;
  // Rendered at the top-right of the title row (e.g. a save bookmark).
  titleAction?: React.ReactNode;
}

const PageHeader: FunctionComponent<Props> = (props) => {
  const analytics = useAnalytics();
  const { stickyNavTriggerClassName } = useNavigationContext();
  const { currentOrigin, currentCommunity } = useCommunityContext();

  const pageHeaderConfig = useComponentConfig(
    "pageHeader",
    props.componentContext || "",
  );

  const {
    borderBottom,
    borderTop,
    center,
    className,
    classNameDescription,
    classNameLinks,
    classNameSubhead,
    classNameTitle,
    classNameMoreLink,
    classNameMoreLinkText,
    classNameContainer,
    description,
    headingLevel: HeadingLevel = "h1",
    isBranded,
    links,
    moreLink,
    moreLinkIcon,
    paddingBottom,
    size = "small",
    subhead,
    customSubhead,
    title,
    sponsorshipAdType,
    titlePrefix,
    titleAction,
  } = useDefaults(props, pageHeaderConfig);

  let descriptionContent: string[];
  if (typeof description === "string") {
    descriptionContent = [description];
  } else {
    descriptionContent = description || [];
  }

  return (
    <div
      className={clsx(
        SELECTORS.PageHeader,
        styles.container,
        {
          [sharedStyles.layout.fancyBorder]:
            borderBottom === "fancy" || borderTop === "fancy",
          [styles.borderTop[size]]: borderTop,
          [styles.borderBottom[size]]: borderBottom,
        },
        paddingBottom && styles.paddingBottom[size],
        className,
      )}
    >
      <div
        className={clsx(
          styles.titleContainer[size],
          sponsorshipAdType && styles.sponsoredHeader,
          sponsorshipAdType && center && styles.centeredSponsoredHeader,
          classNameContainer,
        )}
      >
        {title && (
          <>
            {titlePrefix}
            {isValidElement(title)
              ? title
              : typeof title === "string" && (
                  <DangerouslySetCMSMarkup
                    html={title}
                    component={HeadingLevel}
                    className={clsx(
                      styles.title[size],
                      isBranded && styles.title["branded"],
                      center && styles.centerText,
                      classNameTitle,
                    )}
                  />
                )}
            {sponsorshipAdType && (
              <div>
                <Ad adType={sponsorshipAdType} />
              </div>
            )}
          </>
        )}
        {moreLink && (
          <span className={clsx(styles.moreLinkContainer, classNameMoreLink)}>
            <LinkButton
              href={moreLink.url}
              text={moreLink.text}
              iconRight={moreLinkIcon}
              className={clsx(styles.moreLink[size], classNameMoreLinkText)}
              onClick={() => {
                if (moreLink.event) {
                  analytics?.emit(moreLink.event);
                }
              }}
            />
          </span>
        )}
        {titleAction && (
          <span className={styles.titleAction}>{titleAction}</span>
        )}
      </div>
      {subhead ? (
        typeof subhead === "string" ? (
          <DangerouslySetCMSMarkup
            className={clsx(
              styles.subhead,
              center && styles.centerText,
              classNameSubhead,
            )}
            html={subhead}
          />
        ) : (
          <div
            className={clsx(
              styles.subhead,
              center && styles.centerText,
              classNameSubhead,
            )}
          >
            {subhead}
          </div>
        )
      ) : null}
      {customSubhead}
      {descriptionContent.map((html, idx) => (
        <DangerouslySetCMSMarkup
          key={idx}
          className={clsx(
            SELECTORS.EntryGroupDescription,
            styles.description[size],
            isBranded && styles.description["branded"],
            sharedStyles.text.paragraphSpacing,
            sharedStyles.text.linkStyles,
            center && styles.centerText,
            classNameDescription,
          )}
          html={html}
        />
      ))}
      {links && links.length > 0 && (
        <ul
          className={clsx(
            classNameLinks,
            styles.links,
            center && styles.centerLinks,
          )}
        >
          {links.map((link) => (
            <li key={link.url}>
              <a
                href={getResolvedHref({
                  href: link.url,
                  currentOrigin,
                  currentApexDomain: currentCommunity.domain,
                })}
                className={clsx(
                  styles.link,
                  link.highlight && styles.linkHighlight,
                )}
                onClick={() =>
                  analytics?.emit({
                    event: "subnavigation_click",
                    subnavigation_click: {
                      name: link.text,
                    },
                  })
                }
              >
                {link.text}
              </a>
            </li>
          ))}
        </ul>
      )}

      <div className={stickyNavTriggerClassName} />
    </div>
  );
};

export default PageHeader;
